import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Modal, Input, Button } from './Common';
import NotificationBell from './NotificationBell';
import PasswordPolicyHelp, { passwordFailures } from './PasswordPolicyHelp';
import {
  IconDashboard, IconRequests, IconApplications, IconEngagements,
  IconVulnerabilities, IconRetest, IconGovernance, IconTemplates,
  IconReportTemplates, IconPlaybook, IconAutoScan, IconProxy,
  IconIntegrations, IconBilling, IconNeo, IconClients, IconUsers,
  IconGroups, IconActivity, IconSSO, IconSoW, IconLicensing, IconAI,
  IconCalendar, IconSettings, IconSun, IconMoon, IconLogout, IconKey, IconChevronDown
} from './Icons';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', Icon: IconDashboard, show: () => true },
  { path: '/requests', label: 'Requests', Icon: IconRequests,
    show: (u, g) => g('Super Admins') || g('Requestors') },
  { path: '/applications', label: 'Applications', Icon: IconApplications,
    show: (u, g) => g('Super Admins') || g('Application Managers') },
  // Same gate as Applications: this is the other half of the same asset
  // register, owned by the same group.
  { path: '/networks', label: 'Networks', Icon: IconApplications,
    show: (u, g) => g('Super Admins') || g('Application Managers') },
  { path: '/engagements', label: 'Engagements', Icon: IconEngagements, show: () => true },
  { path: '/calendar', label: 'Calendar', Icon: IconCalendar, show: (u, g) => g('Engagement Admins') || u?.isMsp },
  { path: '/vulnerabilities', label: 'Vulnerabilities', Icon: IconVulnerabilities, show: () => true },
  { path: '/retest', label: 'Retest', Icon: IconRetest, show: (u, g) => g('Penetration Testers') || g('MSP Penetration Testers') || g('Engagement Admins') },
  { path: '/agent-scanner', label: 'AI Pentest Agent', Icon: IconAutoScan, show: (u) => !!u?.aiEnabled },
  { path: '/governance', label: 'Governance', Icon: IconGovernance, show: (u, g) => g('Client Admins') || g('Engagement Admins') },
  { path: '/vuln-templates', label: 'Vuln Templates', Icon: IconTemplates, show: (u, g) => u?.isMsp || g('Super Admins') || g('Engagement Admins') || g('Penetration Testers') },
  { path: '/report-templates', label: 'Report Templates', Icon: IconReportTemplates, show: (u, g) => g('Report Admins') },
  { path: '/sow-templates', label: 'SoW Templates', Icon: IconSoW, show: (u, g) => g('MSP Billing Admins') || g('Super Admins') },
  { path: '/integrations', label: 'Integrations', Icon: IconIntegrations, show: (u, g) => g('Super Admins') || g('Client Admins') },
  { path: '/billing', label: 'Billing', Icon: IconBilling, show: (u, g) => g('MSP Billing Admins') || g('Client Billing Admins') },
];

const ADMIN_ITEMS = [
  { path: '/clients', label: 'Clients', Icon: IconClients, show: (u) => u?.isMsp || u?.groups?.includes('Super Admins') },
  { path: '/admin/users', label: 'User Management', Icon: IconUsers, show: (u, g) => g('Super Admins') || g('Client Admins') },
  { path: '/admin/groups', label: 'Group Management', Icon: IconGroups, show: (u, g) => g('Super Admins') },
  { path: '/admin/activity-log', label: 'Activity Log', Icon: IconActivity,
    show: (u, g) => g('Super Admins') || g('Client Admins') || g('Engagement Admins') },
  { path: '/admin/sso', label: 'SSO Settings', Icon: IconSSO, show: (u, g) => g('Super Admins') },
  { path: '/admin/ai-rules', label: 'AI Rules', Icon: IconAutoScan, show: (u, g) => g('Super Admins') },
  { path: '/admin/statements-of-work', label: 'Statements of Work', Icon: IconSoW, show: (u, g) => g('Client Admins') || g('Client Billing Admins') || g('MSP Billing Admins') },
  { path: '/admin/licensing', label: 'Licensing', Icon: IconLicensing, show: (u, g) => g('Client Admins') },
  { path: '/admin/vendors', label: 'Vendors', Icon: IconIntegrations, show: (u, g) => g('Client Admins') },
  { path: '/admin/ai-knowledge', label: 'AI Knowledge Base', Icon: IconAI, show: (u, g) => g('Super Admins') || g('AI KB Admins') },
];

export default function Layout({ children }) {
  const { user, logout, hasGroup, isReadOnlyViewer, isRequestorOnly, apiFetch } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(() => localStorage.getItem('sidebarExpanded') === 'true');
  const [adminOpen, setAdminOpen] = useState(() => location.pathname.startsWith('/admin') || location.pathname.startsWith('/clients'));
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    setPwError(''); setPwSuccess('');
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      setPwError('All fields are required'); return;
    }
    const failures = passwordFailures(pwForm.newPassword);
    if (failures.length > 0) {
      setPwError('New password does not meet the requirements: ' + failures.map(f => f.label).join('; '));
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwError('New passwords do not match'); return;
    }
    setPwLoading(true);
    try {
      const res = await apiFetch('/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword })
      });
      if (!res.ok) {
        const data = await res.json();
        setPwError(data.error || 'Failed to change password');
      } else {
        setPwSuccess('Password changed successfully');
        setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => { setShowPasswordModal(false); setPwSuccess(''); }, 1500);
      }
    } catch (err) {
      setPwError('Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const currentPage = [...NAV_ITEMS, ...ADMIN_ITEMS].find(n => location.pathname.startsWith(n.path));

  const sw = sidebarExpanded ? 220 : 64;
  const toggleSidebar = () => {
    const next = !sidebarExpanded;
    setSidebarExpanded(next);
    localStorage.setItem('sidebarExpanded', String(next));
  };

  const styles = {
    container: { display: 'flex', minHeight: '100vh' },
    sidebar: {
      width: sw, background: 'var(--bg-sidebar)', color: 'var(--text-sidebar)',
      overflow: 'hidden', display: 'flex', flexDirection: 'column',
      alignItems: sidebarExpanded ? 'stretch' : 'center',
      position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
      borderRight: '1px solid var(--border)', padding: '12px 0',
      transition: 'width 0.2s ease'
    },
    navItem: (active) => ({
      width: sidebarExpanded ? 'auto' : 40, height: 40,
      display: 'flex', alignItems: 'center',
      justifyContent: sidebarExpanded ? 'flex-start' : 'center',
      gap: 10, padding: sidebarExpanded ? '0 14px' : 0,
      cursor: 'pointer', borderRadius: 10,
      margin: sidebarExpanded ? '1px 8px' : '2px 0',
      transition: 'all 0.15s',
      background: active ? 'var(--nav-active-bg)' : 'transparent',
      color: active ? 'var(--nav-active-text)' : 'var(--text-sidebar)',
      textDecoration: 'none', fontSize: 13, fontWeight: active ? 600 : 500,
      whiteSpace: 'nowrap', overflow: 'hidden',
    }),
    main: { flex: 1, marginLeft: sw, transition: 'margin-left 0.2s ease' },
    topbar: {
      height: 48, background: 'var(--bg-card)', borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px',
      position: 'sticky', top: 0, zIndex: 50
    },
    content: { padding: 20 },
    btn: {
      padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
      fontSize: 12, fontWeight: 500, transition: 'all 0.15s', display: 'inline-flex',
      alignItems: 'center', gap: 5
    },
    userInfo: { display: 'flex', alignItems: 'center', gap: 8 },
  };

  return (
    <div style={styles.container}>
      <aside style={styles.sidebar}>
        {/* Logo + toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexShrink: 0, padding: sidebarExpanded ? '0 14px' : 0, width: '100%', justifyContent: sidebarExpanded ? 'flex-start' : 'center' }}>
          <div onClick={toggleSidebar} style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #4f8fff, #6366f1)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          {sidebarExpanded && <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>AttackPath</span>}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: sidebarExpanded ? 'stretch' : 'center', gap: 2, overflowY: 'auto' }}>
          {NAV_ITEMS.filter(n => n.show(user, hasGroup))
            .filter(n => !isReadOnlyViewer || n.path === '/engagements')
            .filter(n => !isRequestorOnly || n.path === '/requests')
            .map(n => (
            <Link key={n.path} to={n.path} style={styles.navItem(location.pathname.startsWith(n.path))} title={sidebarExpanded ? undefined : n.label}>
              <n.Icon size={20} style={{ flexShrink: 0 }} />
              {sidebarExpanded && <span>{n.label}</span>}
            </Link>
          ))}
          {ADMIN_ITEMS.some(n => n.show(user, hasGroup)) && (
            <>
              <div style={{ width: sidebarExpanded ? 'calc(100% - 16px)' : 24, borderTop: '1px solid var(--border)', margin: sidebarExpanded ? '6px 8px' : '6px 0' }} />
              <div onClick={() => setAdminOpen(!adminOpen)} title={sidebarExpanded ? undefined : 'Administration'}
                style={{ ...styles.navItem(location.pathname.startsWith('/admin') || location.pathname.startsWith('/clients')), cursor: 'pointer' }}>
                <IconSettings size={20} style={{ flexShrink: 0 }} />
                {sidebarExpanded && (
                  <>
                    <span style={{ flex: 1 }}>Admin</span>
                    <span style={{ transition: 'transform 0.2s', transform: adminOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'flex' }}><IconChevronDown /></span>
                  </>
                )}
              </div>
              {adminOpen && ADMIN_ITEMS.filter(n => n.show(user, hasGroup)).map(n => (
                <Link key={n.path} to={n.path} style={styles.navItem(location.pathname.startsWith(n.path))} title={sidebarExpanded ? undefined : n.label}>
                  <n.Icon size={18} style={{ flexShrink: 0 }} />
                  {sidebarExpanded && <span>{n.label}</span>}
                </Link>
              ))}
            </>
          )}
        </nav>

        {/* User */}
        <div title={user?.fullName || user?.username} style={{
          display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexShrink: 0,
          padding: sidebarExpanded ? '0 14px' : 0, width: '100%', justifyContent: sidebarExpanded ? 'flex-start' : 'center',
        }}>
          <div style={{
            width: 32, height: 32, background: 'linear-gradient(135deg, #4f8fff, #6366f1)', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
            color: 'white', cursor: 'pointer', flexShrink: 0
          }}>
            {(user?.fullName || user?.username || 'U').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
          </div>
          {sidebarExpanded && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.fullName || user?.username}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{user?.isMsp ? 'MSP' : 'Client'}</div>
            </div>
          )}
        </div>
      </aside>
      <div style={styles.main}>
        <header style={styles.topbar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.2px' }}>{currentPage?.label || 'AttackPath'}</span>
          </div>
          <div style={styles.userInfo}>
            <Link to="/help" title="Help Center" style={{ ...styles.btn, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border)', padding: '6px 8px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </Link>
            <NotificationBell />
            <button onClick={toggle} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              style={{ ...styles.btn, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border)', padding: '6px 8px' }}>
              {theme === 'dark' ? <IconSun size={14} /> : <IconMoon size={14} />}
            </button>
            <button onClick={() => { setShowPasswordModal(true); setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setPwError(''); setPwSuccess(''); }}
              title="Change Password"
              style={{ ...styles.btn, background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border)', padding: '6px 8px' }}>
              <IconKey size={14} />
            </button>
            <button onClick={handleLogout}
              style={{ ...styles.btn, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
              <IconLogout size={14} />
            </button>
          </div>
        </header>
        <main style={styles.content}>{children}</main>
      </div>

      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Change Password" width={440}>
        {pwError && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', marginBottom: 16, fontSize: 14 }}>
            {pwError}
          </div>
        )}
        {pwSuccess && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', marginBottom: 16, fontSize: 14 }}>
            {pwSuccess}
          </div>
        )}
        <Input label="Current Password" type="password" value={pwForm.currentPassword}
          onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} />
        <Input label="New Password" type="password" value={pwForm.newPassword}
          onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} />
        <PasswordPolicyHelp password={pwForm.newPassword} />
        <Input label="Confirm New Password" type="password" value={pwForm.confirmPassword}
          onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))} />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
          <Button onClick={handleChangePassword} disabled={pwLoading}>
            {pwLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
